import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { dispatch, select } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { AuthService } from '../../auth/services/auth.service';
import { FileService } from '../../shared/services/file-service';
import { SearchService } from '../../shared/services/search.service';
import type { ISearchParams } from '../../shared/interfaces/ISearchParams';
import { Role } from '../../shared/enums/role.enum';
import { ResultItemComponent } from './result-item/result-item.component';
import { ResultItemType } from '../../shared/enums/result-item-type.enum';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { ClearSearchResults, LoadSearchResults } from '../../shared/state/search-results/search-results.actions';
import { SearchResultsState } from '../../shared/state/search-results/search-results.state';
import { SiIconComponent } from '@siemens/element-ng/icon';

@Component({
    selector: 'app-search-results',
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ResultItemComponent, TranslatePipe, SiLoadingSpinnerComponent, SiIconComponent]
})
export class SearchResultsComponent implements OnInit {
    readonly ResultItemType = ResultItemType;

    private readonly destroyRef = inject(DestroyRef);
    private readonly searchService = inject(SearchService);
    private readonly fileService = inject(FileService);
    private readonly authService = inject(AuthService);
    private readonly translate = inject(TranslateService);
    private readonly clearSearchResults = dispatch(ClearSearchResults);
    private readonly loadSearchResults = dispatch(LoadSearchResults);

    protected readonly countries = select(SearchResultsState.countries);
    protected readonly locations = select(SearchResultsState.locations);
    protected readonly isLoading = select(SearchResultsState.isLoading);
    protected readonly resultsSum = computed(() => this.countries().length + this.locations().length);
    protected readonly hasLocations = computed(() => this.locations().length > 0);
    protected readonly hasSearched = signal(false);

    public ngOnInit(): void {
        this.initSearchParamsListener();
    }

    protected download(): void {
        const advancedUserRoles = [Role.Administrator];
        if (advancedUserRoles.includes(this.authService.getRole())) {
            this.fileService.jsonToCSV(this.locations(), true);
            return;
        }

        this.fileService.jsonToCSV(this.locations(), false);
    }

    private initSearchParamsListener(): void {
        this.searchService.currentSearchParams
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(searchParams => this.updateSearchResults(searchParams))
            )
            .subscribe();
    }

    private updateSearchResults(searchParams: ISearchParams | null): void {
        if (!searchParams) {
            this.hasSearched.set(false);
            void this.clearSearchResults();
            return;
        }

        const language = (this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'en').toLowerCase();
        this.loadSearchResults({ searchParams, language }).then(() => this.hasSearched.set(true));
    }
}
